import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Sale } from '../SalesSegment/SalesSegment';
import { Modal } from '../Modal/Modal';
import CustomDatePicker from '../CustomDatePicker/CustomDatePicker';
import SmallCheckBox from '../SmallCheckBox/SmallCheckBox';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import SecondaryButton from '../SecondaryButton/SecondaryButton';
import { format, parseISO } from 'date-fns';

export interface AddToProductionFormData {
  deliveryDate: string;
  prioritized: boolean;
  pendingMeasures: boolean;
}

type Props = {
  sale: Sale | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: AddToProductionFormData) => void;
  isLoading?: boolean;
};

function AddToProductionModal({ sale, isOpen, onClose, onSubmit, isLoading = false }: Props) {
  const { t } = useTranslation();
  const [deliveryDate, setDeliveryDate] = useState<Date | null>(null);
  const [prioritized, setPrioritized] = useState(false);
  const [pendingMeasures, setPendingMeasures] = useState(false);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    if (sale && sale.weddingDate) {
      // Set delivery date to wedding date by default, or a week before if wedding date is in the future
      try {
        let weddingDate: Date;
        // Handle both ISO strings and Unix timestamps (in seconds or milliseconds)
        if (typeof sale.weddingDate === 'string') {
          // Check if it's a numeric string (timestamp)
          const numValue = Number(sale.weddingDate);
          if (!isNaN(numValue) && numValue > 0) {
            // It's a timestamp - check if it's in seconds (less than year 2000 in ms) or milliseconds
            weddingDate = numValue < 946684800000 ? new Date(numValue * 1000) : new Date(numValue);
          } else {
            // Try parsing as ISO string
            weddingDate = parseISO(sale.weddingDate);
          }
        } else {
          // If it's already a number
          const numValue = Number(sale.weddingDate);
          weddingDate = numValue < 946684800000 ? new Date(numValue * 1000) : new Date(numValue);
        }
        
        const defaultDeliveryDate = weddingDate > new Date() 
          ? new Date(weddingDate.getTime() - 7 * 24 * 60 * 60 * 1000) // 7 days before wedding
          : new Date(); // Today if wedding date is in the past
        setDeliveryDate(defaultDeliveryDate);
      } catch (error) {
        console.error('Error parsing wedding date:', error, sale.weddingDate);
        setDeliveryDate(new Date());
      }
    } else if (sale) {
      setDeliveryDate(new Date());
    }
  }, [sale]);

  useEffect(() => {
    setIsValid(deliveryDate !== null);
  }, [deliveryDate]);

  const handleSubmit = () => {
    if (!deliveryDate || !sale) return;
    
    const formData: AddToProductionFormData = {
      deliveryDate: format(deliveryDate, 'yyyy-MM-dd'),
      prioritized,
      pendingMeasures,
    };
    
    onSubmit(formData);
  };

  const handleClose = () => {
    setPrioritized(false);
    setPendingMeasures(false);
    setDeliveryDate(null);
    onClose();
  };

  const getWeddingDateDisplay = (): string | null => {
    if (!sale?.weddingDate) return null;
    try {
      let weddingDate: Date;
      // Handle both ISO strings and Unix timestamps (in seconds or milliseconds)
      if (typeof sale.weddingDate === 'string') {
        // Check if it's a numeric string (timestamp)
        const numValue = Number(sale.weddingDate);
        if (!isNaN(numValue) && numValue > 0) {
          // It's a timestamp - check if it's in seconds (less than year 2000 in ms) or milliseconds
          weddingDate = numValue < 946684800000 ? new Date(numValue * 1000) : new Date(numValue);
        } else {
          // Try parsing as ISO string
          weddingDate = parseISO(sale.weddingDate);
        }
      } else {
        // If it's already a number
        const numValue = Number(sale.weddingDate);
        weddingDate = numValue < 946684800000 ? new Date(numValue * 1000) : new Date(numValue);
      }
      return format(weddingDate, 'dd/MM/yyyy');
    } catch (error) {
      console.error('Error formatting wedding date:', error, sale.weddingDate);
      return sale.weddingDate; // Fallback to raw string
    }
  };

  if (!isOpen || !sale) {
    return null;
  }

  const weddingDateDisplay = getWeddingDateDisplay();

  return (
    <Modal
      modalTitle={t("sales.actions.add_to_production") || "Add to Production"}
      canClose
      onClose={handleClose}
    >
      <div className="space-y-5 p-5">
        {weddingDateDisplay && (
          <div className="bg-blue-50 border border-blue-200 rounded p-3">
            <p className="text-sm text-blue-800">
              <strong>{t("contracts.wedding_date") || "Wedding Date"}:</strong>{' '}
              {weddingDateDisplay}
            </p>
          </div>
        )}

        <div>
          <CustomDatePicker
            value={deliveryDate}
            onChange={(date) => setDeliveryDate(date)}
            label={t("orders.orders_basket.add_form.delivery_time") || "Shipping Date"}
            dateFormat="dd/MM/yyyy"
            isDisabled={isLoading}
            disablePast={false}
          />
        </div>

        <div className="space-y-3">
          <SmallCheckBox
            label={t("orders.actions.priority") || "Set as High priority"}
            onChange={(e) => setPrioritized(e.target.checked)}
            defaultValue={prioritized}
            isDisabled={isLoading}
          />
          
          <SmallCheckBox
            label={t("orders.actions.measures") || "Set as Pending client measures"}
            onChange={(e) => setPendingMeasures(e.target.checked)}
            defaultValue={pendingMeasures}
            isDisabled={isLoading}
          />
        </div>

        <div className="flex space-x-3 justify-end pt-5 border-t">
          <SecondaryButton
            text={t("global.cancel") || "Cancel"}
            fn={handleClose}
            isDisabled={isLoading}
          />
          <PrimaryButton
            text={t("global.confirm") || "Confirm"}
            fn={handleSubmit}
            isDisabled={!isValid || isLoading}
          />
        </div>
      </div>
    </Modal>
  );
}

export default AddToProductionModal;
